import React, { useContext, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { CurrentPageContext } from 'app/home/general-components/HistoryStackContext';
import FullPageTransition from 'app/home/general-components/FullPageTransition';
import { v4 as uuidv4 } from 'uuid';

import { SelectDeviceType, SelectDeviceToAdd, MultiDeviceStatus, ChooseDeviceName, SelectDeviceTypeL2, ResetDeviceInstruction, SelectCurtainNumChannels, ChooseScenarioSwitchConfig, SelectDeviceCategory } from './pages/Devices';
import { ConnectingToDevice, ErrorOnAddDevice } from './pages/Status';
import { AskWifiManualESPTouch, ConnectToWifiNetwork } from './pages/Wifi';


import './AddDevice.scss'
import { DeviceType } from 'app/model/Device';

export const AddDeviceStatus = {
    notSelected: "notSelected",
    pairing: "pairing",
    joined: "joined",
    connectionError: "connectionError",
    unknownError: "unknownError",
}

export default function AddDevice({ gateway }) {
    const p = useContext(CurrentPageContext);
    const dismissHandler = p.dismiss;
    const roomIdToAddTo = p.state.roomId;

    const isSolo = gateway.isSolo;

    const [stage, setStage] = useState('select-category');
    const [candidateDevices, setCandidateDevices] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(undefined);
    const [selectedType, setSelectedType] = useState(undefined);
    const [selectedDevice, setSelectedDevice] = useState(undefined); // when user clicks on a single device
    const [searching, setSearching] = useState(false);
    const [publicErrorId, setPublicErrorId] = useState(undefined);
    const [searchDuration, setSearchDuration] = useState(undefined);

    const [connectedSSIDInfo, setConnectedSSIDInfo] = useState(gateway.connectedSSIDInfo);
    useEffect(() => {
        setConnectedSSIDInfo(gateway.connectedSSIDInfo);
    }, [gateway.connectedSSIDInfo])

    const prepare = gateway.addDeviceController.prepare;
    // useEffect(() => {
    //     prepare();
    // }, [prepare])

    const onCategorySelection = useCallback((categoryInfo) => {
        setSelectedCategory(categoryInfo);
        setStage('select-type');
    }, []);

    const onTypeSelection = useCallback((typeInfo) => {
        setSelectedType(typeInfo);
        if (typeInfo.hasLevel2) {
            setStage('select-type-l2');
        } else {
            setStage('reset-device');
            prepare();
        }
    }, [prepare]);

    const confirmDeviceReset = useCallback(() => {
        if (selectedType.wifi) {
            if (isSolo) {
                setStage('get-wifi-password');
            } else {
                if ((connectedSSIDInfo !== undefined) && (connectedSSIDInfo.name !== undefined)) {
                    setStage('get-wifi-password');
                } else {
                    setStage('ask-wifi-manual-esptouch');
                }
            }
        } else {
            setSearching(true);
            setStage('select-device');
            gateway.addDeviceController.search(selectedType.searchTypes, undefined, undefined);
        }
    }, [selectedType, gateway.addDeviceController, connectedSSIDInfo, isSolo]);

    const onPasswordEntered = useCallback((wifi, password, bssid) => { // bssid can be undefined
        setSearching(true);
        setStage('select-device');
        gateway.addDeviceController.search(selectedType.searchTypes, wifi, password, bssid);
    }, [gateway.addDeviceController, selectedType]);
    
    const startSearchWithoutESPTouch = useCallback(() => {
        setSearching(true);
        setStage('select-device');
        gateway.addDeviceController.search(selectedType.searchTypes);
    }, [gateway.addDeviceController, selectedType]);

    const onDeviceSelection = useCallback((device) => {
        setSelectedDevice(device);

        if (device.type === DeviceType.curtain) {
            if (device.num_channels === undefined) {
                setStage('select-curtain-num_channels');
            } else {
                setStage('connect-to-device');
                gateway.addDeviceController.join(device.deviceId, roomIdToAddTo, { num_channels: device.num_channels });
            }
        } else {
            setStage('connect-to-device');
            gateway.addDeviceController.join(device.deviceId, roomIdToAddTo);
        }
    }, [gateway.addDeviceController, roomIdToAddTo]);

    const selectAllDevices = useCallback((config) => {
        if (config === undefined) {
            if (selectedType.searchTypes.includes(DeviceType.curtain)) {
                setStage('select-curtain-num_channels');
            } else {
                setStage('multi-device-status');
                let newCandidateDevices = [];
                for (let i = 0; i < candidateDevices.length; i++) {
                    const device = candidateDevices[i];
                    let newDevice = { ...device }
                    newDevice.addDeviceStatus = AddDeviceStatus.pairing;
                    newCandidateDevices.push(newDevice);
                    gateway.addDeviceController.join(newDevice.deviceId, roomIdToAddTo);
                }
                setCandidateDevices(newCandidateDevices);
            }
        } else {
            setStage('multi-device-status');
            let newCandidateDevices = [];
            for (let i = 0; i < candidateDevices.length; i++) {
                const device = candidateDevices[i];
                let newDevice = { ...device }
                newDevice.addDeviceStatus = AddDeviceStatus.pairing;
                if (selectedType.searchTypes.includes(DeviceType.curtain)) {
                    newDevice.num_channels = config.num_channels;
                }
                newCandidateDevices.push(newDevice);
                gateway.addDeviceController.join(newDevice.deviceId, roomIdToAddTo, config);
            }
            setCandidateDevices(newCandidateDevices);
        }
    }, [gateway.addDeviceController, selectedType, candidateDevices, roomIdToAddTo]);

    const onNameSpecified = useCallback((name) => {
        const mac = selectedDevice.mac;
        if (mac !== undefined) {
            const devicesWithThisMac = gateway.devices.filter(device => device.mac === mac);
            if (devicesWithThisMac.length > 0) {
                devicesWithThisMac[0].rename(name);
            }
        }
    }, [selectedDevice, gateway.devices]);

    const gwDevice = useMemo(() => {
        if (selectedDevice !== undefined) {
            const mac = selectedDevice.mac;
            if (mac !== undefined) {
                const devicesWithThisMac = gateway.devices.filter(device => device.mac === mac);
                if (devicesWithThisMac.length > 0) {
                    return devicesWithThisMac[0];
                }
            }
        }
        return undefined;
    }, [selectedDevice, gateway.devices]);

    const currentDeviceName = useMemo(() => {
        if (selectedDevice !== undefined) {
            const mac = selectedDevice.mac;
            if (mac !== undefined) {
                const devicesWithThisMac = gateway.devices.filter(device => device.mac === mac);
                if (devicesWithThisMac.length > 0) {
                    if (devicesWithThisMac[0].name !== undefined)
                        return devicesWithThisMac[0].name;
                }
            }
        }
        return '';
    }, [selectedDevice, gateway.devices]);

    const onRetry = useCallback(() => {
        setSearching(false);
        setCandidateDevices([]);
        setSelectedType(undefined);
        setSelectedCategory(undefined);
        setPublicErrorId(undefined);
        setSelectedDevice(undefined);
        setStage('select-category');
    }, []);

    const sendEvent = useRef(() => { });
    useEffect(() => {
        sendEvent.current = gateway.sendEvent;
    }, [gateway.sendEvent])

    const notificationCallback = useCallback((msg) => {
        switch (msg.sub_event) {
            case "_webview-got-wifi-info":
                setConnectedSSIDInfo({
                    connected: true,
                    remembered_password: false,
                    name: msg.ssid,
                    bssid: msg.bssid,
                    error: undefined,
                });
                break;
            case "device_found_notification":
                if ((selectedType !== undefined) &&
                    (selectedType.searchTypes !== undefined) &&
                    (selectedType.searchTypes.includes(msg.device_type))) {
                    let device_id = msg.device_id;
                    setCandidateDevices(ds => [...(ds.filter(d => (d.deviceId !== device_id))), {
                        deviceId: device_id,
                        addDeviceStatus: AddDeviceStatus.notSelected,

                        mac: device_id,
                        name: msg.name,
                        type: msg.device_type,
                        indicate: (() => {
                            gateway.addDeviceController.indicate(device_id)
                            // sendEvent.current('indicate_device', {
                            //     device_id: device_id,
                            // });
                        }),
                        waitingForChangeState: {},
                    }]);
                }
                break;
            case "error":
                if ((msg.device_id === undefined) || (selectedDevice !== undefined)) { // a public error
                    if ((stage === 'select-device') || (stage === 'connect-to-device') || (stage === 'get-wifi-password')) {
                        setPublicErrorId(msg.error);
                        setStage('error');
                    }
                } else { // an error for a specific device (in multi-device page)
                    setCandidateDevices(ds => ds.map(d => {
                        if (d.deviceId !== msg.device_id) {
                            return d;
                        }
                        const newD = { ...d };
                        if (msg.error === 2) {
                            newD.addDeviceStatus = AddDeviceStatus.connectionError;
                        } else {
                            newD.addDeviceStatus = AddDeviceStatus.unknownError;
                        }
                        return newD;
                    }));
                }
                break;
            case "search_has_ended":
                setSearching(false);
                if (candidateDevices.length === 0) {
                    if (stage === 'select-device') {
                        setPublicErrorId("not_found");
                        setStage('error');
                    }
                }
                break;
            case "search_has_started":
                // console.log(msg.search_duration);
                setSearchDuration(msg.search_duration);
                break;
            case "device_joined_notification":
                if (selectedDevice && (selectedDevice.deviceId === msg.device_id)) {
                    setSelectedDevice({ ...selectedDevice, mac: msg.mac });
                    if (selectedDevice.type === DeviceType.scenarioSwitch) {
                        setStage('choose-config-for-scenario-switch');
                    } else {
                        setStage('choose-name');
                    }
                } else {
                    setCandidateDevices(ds => ds.map(d => {
                        if (d.deviceId !== msg.device_id) {
                            return d
                        }
                        const newD = { ...d }
                        newD.mac = msg.mac;
                        newD.indicate = () => {
                            sendEvent.current('indicate_device', {
                                mac: newD.mac,
                            });
                        }
                        newD.addDeviceStatus = AddDeviceStatus.joined;
                        return newD;
                    }));
                }
                break;
            default:
                break;
        }
    }, [selectedType, selectedDevice, candidateDevices, stage, gateway.addDeviceController]);

    const notificationCallbackId = useState(uuidv4())[0];
    useEffect(() => {
        gateway.addDeviceController.registerNotification(notificationCallbackId, notificationCallback);
        return () => {
            gateway.addDeviceController.unregisterNotification(notificationCallbackId);
        }
    }, [gateway.addDeviceController, notificationCallbackId, notificationCallback]);


    return (
        <div className="add-device-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />

            <div className="bg-blank" style={{ zIndex: -9, opacity: (stage === "error") ? 1 : 0, transition: "opacity 0.5s linear" }} />
            <div className="bg-failure-color" style={{ zIndex: -8, opacity: (stage === "error") ? 1 : 0, transition: "opacity 0.5s linear" }} />

            <FullPageTransition disableOnFirstTime>
                <SelectDeviceCategory
                    dismissHandler={dismissHandler}
                    isVisible={stage === "select-category"}
                    onCategorySelection={onCategorySelection}
                    negotiatedProtocol={gateway.negotiatedProtocol}
                    key="add-device-page-select-category" />

                <SelectDeviceType
                    dismissHandler={dismissHandler}
                    isVisible={stage === "select-type"}
                    selectedCategory={selectedCategory}
                    onTypeSelection={onTypeSelection}
                    key="add-device-page-select-type" />

                <SelectDeviceTypeL2
                    dismissHandler={dismissHandler}
                    isVisible={stage === "select-type-l2"}
                    onTypeSelection={onTypeSelection}
                    selectedTypeCategory={selectedType}
                    key="add-device-page-select-type-l2" />

                <ResetDeviceInstruction
                    dismissHandler={dismissHandler}
                    isVisible={stage === "reset-device"}
                    selectedType={selectedType}
                    confirmDeviceReset={confirmDeviceReset}
                    key="add-device-page-reset-device" />

                <ConnectToWifiNetwork
                    dismissHandler={dismissHandler}
                    isVisible={stage === "get-wifi-password"}
                    connectedSSIDInfo={connectedSSIDInfo} isSolo={isSolo}
                    onPasswordEntered={onPasswordEntered}
                    setErrorId={setPublicErrorId} setStage={setStage}
                    key="add-device-page-get-wifi-password" />
                
                <AskWifiManualESPTouch
                    dismissHandler={dismissHandler}
                    isVisible={stage === "ask-wifi-manual-esptouch"}
                    connectedSSIDInfo={connectedSSIDInfo} 
                    startSearchWithoutESPTouch={startSearchWithoutESPTouch}
                    key="add-device-page-get-wifi-password" />

                <SelectDeviceToAdd
                    dismissHandler={dismissHandler}
                    isVisible={stage === "select-device"}
                    onDeviceSelection={onDeviceSelection}
                    selectAllDevices={selectAllDevices}
                    candidateDevices={candidateDevices}
                    selectedType={selectedType}
                    searching={searching}
                    searchDuration={searchDuration}
                    key="add-device-page-select-device" />

                <MultiDeviceStatus
                    dismissHandler={dismissHandler}
                    isVisible={stage === "multi-device-status"}
                    candidateDevices={candidateDevices}
                    key="add-device-multi-device-status" />

                <SelectCurtainNumChannels
                    dismissHandler={dismissHandler}
                    isVisible={stage === "select-curtain-num_channels"}
                    onDeviceSelection={onDeviceSelection}
                    selectAllDevices={selectAllDevices}
                    selectedDevice={selectedDevice}
                    key="add-device-page-select-curtain-channels" />

                <ConnectingToDevice
                    dismissHandler={dismissHandler}
                    isVisible={stage === "connect-to-device"}
                    selectedType={selectedType}
                    key="add-device-page-connect-to-device" />

                <ChooseDeviceName
                    dismissHandler={dismissHandler}
                    isVisible={stage === "choose-name"}
                    selectedDevice={selectedDevice}
                    onNameSpecified={onNameSpecified} currentDeviceName={currentDeviceName}
                    key="add-device-page-choose-name" />

                <ChooseScenarioSwitchConfig
                    dismissHandler={dismissHandler}
                    isVisible={stage === "choose-config-for-scenario-switch"}
                    selectedDevice={selectedDevice}
                    onNameSpecified={onNameSpecified} currentDeviceName={currentDeviceName}
                    gwDevice={gwDevice} scenarios={gateway.scenarios}
                    key="add-device-page-choose-name" />

                <ErrorOnAddDevice
                    dismissHandler={dismissHandler}
                    isVisible={stage === "error"}
                    errorId={publicErrorId} onRetry={onRetry}
                    key="add-device-page-error" />
            </FullPageTransition>
        </div>
    );
}


